import type { ZoneDuTuto } from '../partage/api/types';

/**
 * Où est, dans la page, ce que le tuto veut éclairer ou entourer (ticket 33).
 *
 * Le serveur dit une zone par son nom (« le débloquant », « la bulle x », « le bouton
 * « c'est fait » ») ; ce fichier seul sait la retrouver dans l'écran. La zone suit le **vrai
 * élément**, jamais des coordonnées : elle tient à toute taille de fenêtre. Une zone absente
 * (pas de débloquant, une bulle cachée dans une étape pas ouverte) rend `null`, et le tuto
 * s'en passe.
 *
 * Il la trouve par un **repère** posé exprès pour lui sur l'élément, `data-tuto="<clé>"`, jamais
 * par les classes CSS des écrans (ticket 00 du chantier `telephone`) : un écran refait peut
 * renommer ses classes sans casser une étape en silence. Chaque repère cherché ici doit être
 * posé par un composant, `tests/client/aide/reperesDuTuto.test.ts` le vérifie. Une zone qui vise une
 * bulle, un dossier ou une tâche précise garde son identifiant (`data-bulle`, `data-dossier`,
 * `data-tache`, `data-journee-rond`), à côté du repère.
 */

/** Le sélecteur d'un repère du tuto. */
export const repere = (cle: string) => `[data-tuto="${cle}"]`;

/** Un élément qu'on voit : dans la page, avec une taille, et pas caché par la carte. */
function visible(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  // Une bulle cachée de la carte porte `aria-hidden` (`NoeudCarte.tsx`), et elle seule.
  if (el.closest(`${repere('bulle')}[aria-hidden="true"]`)) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function premier(selecteur: string, racine: ParentNode = document): HTMLElement | null {
  for (const el of racine.querySelectorAll(selecteur)) if (visible(el)) return el;
  return null;
}

const net = (t: string | null | undefined) => (t ?? '').replace(/\s+/g, ' ').trim();

/**
 * Le texte d'un bouton, d'un onglet ou d'un menu, sans égard à la casse : le tuto dit
 * « C'est fait » avec sa majuscule, comme l'écran (retour 9 des testeurs), et une majuscule
 * de plus ou de moins ne doit jamais perdre le geste. Jamais pour un titre de l'utilisateur,
 * qui se compare à la lettre (`net`).
 */
const libelle = (t: string | null | undefined) => net(t).toLocaleLowerCase('fr-FR');

/**
 * Ce qui porte le contenu de l'utilisateur, jamais un bouton de l'app : une bulle sur la carte,
 * en Hiérarchie ou en Liste, une ligne de « Projets ». Le mot « Il faut y aller » de la ligne
 * des réglages d'une bulle n'est pas le bouton « Il faut y aller » de la fiche (ticket 17 de
 * retours-testeurs, point 3 : paramètres fermés, l'anneau entourait une bulle de la carte).
 */
const CONTENU = [repere('bulle'), repere('bulle-hierarchie'), repere('bulle-liste'), repere('dossier')].join(', ');

/**
 * Le bouton qui porte ce mot. Dans la fenêtre ouverte d'abord, puis la fiche, puis le reste :
 * « C'est fait » est celui de la fiche, pas celui d'une ligne derrière elle. Le mot exact
 * l'emporte sur un mot qui le contient (« Long » avant « Longtemps »). Une bulle n'est jamais
 * prise pour un bouton, même si son texte contient le mot.
 */
function boutonNomme(texte: string): HTMLElement | null {
  const voulu = libelle(texte);
  const racines = [
    document.querySelector('[role="dialog"]'),
    document.querySelector(repere('fiche')),
    document,
  ].filter((r): r is Element | Document => r !== null);
  for (const racine of racines) {
    const boutons = [...racine.querySelectorAll('button, [role="tab"], [role="menuitem"]')]
      .filter(visible)
      .filter((b) => !b.closest(CONTENU));
    const exact = boutons.find((b) => libelle(b.textContent) === voulu);
    if (exact) return exact;
    const contient = boutons.find((b) => libelle(b.textContent).includes(voulu));
    if (contient) return contient;
  }
  return null;
}

/**
 * Une ligne de dossier dans « Projets » : par son identifiant s'il est dit (ticket 44, un
 * homonyme n'est jamais pris à sa place), sinon par son titre, sinon la première.
 */
function ligneDeDossier({ id, texte: titre }: ZoneDuTuto): HTMLElement | null {
  const rangees = [...document.querySelectorAll<HTMLElement>(repere('dossier'))].filter(visible);
  if (id) return rangees.find((r) => r.dataset.dossier === id) ?? null;
  if (!titre) return rangees[0] ?? null;
  return rangees.find((r) => net(r.querySelector(repere('dossier-titre'))?.textContent) === net(titre)) ?? null;
}

/** Ce qui est dans la fiche ouverte. */
const dansLaFiche = (cle: string) => `${repere('fiche')} ${repere(cle)}`;

/*
 * Les boutons de l'ancienne fiche que l'Aide nommait encore (« Changer », « En réflexion »,
 * « Lier ») menaient, depuis le chantier `revue-design`, à ce qui joue leur rôle dans la fiche
 * d'aujourd'hui. Depuis le chantier `aide` (ticket 07), plus aucun texte ne les dit : l'Aide nomme
 * « Idée » et « + Condition », qui se trouvent par leur mot, et ces renvois sont partis.
 */

export function trouverLaZone(zone: ZoneDuTuto): HTMLElement | null {
  switch (zone.cle) {
    // ── le menu ──
    case 'menu':
      return premier(repere('menu'));
    // Les liens du menu, en haut ou, au pouce, en bas : par leur repère, pas par leur mot.
    case 'menu-maintenant':
      return premier(repere('menu-maintenant'));
    case 'menu-projets':
      return premier(repere('menu-projets'));
    case 'menu-agenda':
      return premier(repere('menu-agenda'));
    case 'ampoule':
      return premier(repere('ampoule'));

    // ── Maintenant ──
    case 'maintenant-tete':
      // Sans projet, « Maintenant » accueille avec « Par quoi tu commences ? » (ticket 14) :
      // son dernier temps dit ce que la page deviendra.
      return premier(repere('maintenant-tete')) ?? premier(repere('premier-temps-ici'));
    case 'pastille-dossiers':
      return premier(repere('pastille-dossiers'));
    // Un projet du menu « Tous les projets », ouvert (chantier `aide`, ticket 03), par son identifiant.
    case 'dossier-du-menu':
      return premier(`${repere('dossier-du-menu')}[data-dossier="${CSS.escape(zone.id ?? '')}"]`);
    case 'debloquant-premier':
      return premier(repere('debloquant-premier'));
    case 'prioritaire':
      // Vide, Prioritaire ne s'affiche plus (chantier revue-design, ticket 01) : l'étape vise
      // alors la colonne de droite, où il se pose quand il a quelque chose.
      return (
        premier(`${repere('maintenant')} ${repere('groupe-prioritaire')}`) ??
        premier(`${repere('maintenant')} ${repere('maintenant-droite')}`)
      );
    case 'prioritaire-place':
      // Prioritaire vide (ticket 02 rouvert, décision d'Igor du 2026-10-04) : le haut de la
      // colonne de droite, la tête de « Pendant ce temps », là où le bloc se poserait.
      return premier(`${repere('maintenant')} ${repere('pendant-tete')}`);
    case 'en-attendant':
      return premier(`${repere('maintenant')} ${repere('groupe-en-attendant')}`);
    case 'a-verifier':
      return premier(`${repere('maintenant')} ${repere('groupe-a-verifier')}`);
    case 'bouton-filtrer':
      return premier(repere('bouton-filtrer'));
    // « Ma journée » (ticket 16) : le bloc, et le rond d'une de ses tâches.
    case 'journee':
      return premier(`${repere('maintenant')} ${repere('journee')}`);
    case 'journee-rond':
      return [...document.querySelectorAll<HTMLElement>(`${repere('journee-rond')}[data-journee-rond]`)]
        .filter(visible)
        .find((el) => el.dataset.journeeRond === zone.id) ?? null;
    case 'panneau-du-jour':
      return dansLePanneauDuJour(zone.id ?? '');
    case 'reglage-temps': {
      // « J'ai » des réglages (chantier `aide`, ticket 04) : déplié, le choix nommé ; sinon le
      // bouton qui le déplie.
      const reglage = premier(repere('reglage-temps'));
      if (!reglage) return null;
      const choix = [...reglage.querySelectorAll('[role="menuitemradio"]')]
        .filter(visible)
        .find((b) => libelle(b.textContent) === libelle(zone.texte));
      return choix ?? premier('[aria-haspopup="menu"]', reglage);
    }

    // ── Projets ──
    case 'dossier': {
      // La partie qui ouvre le dossier, sans la petite poubelle au bout de la ligne : dans une
      // étape « clique », seule la zone reste cliquable.
      const ligne = ligneDeDossier(zone);
      return ligne ? (premier(repere('dossier-carte'), ligne) ?? ligne) : null;
    }
    case 'dossier-acquis':
      return premier(repere('dossier-acquis'), ligneDeDossier(zone) ?? document);
    case 'dossier-tot': {
      const ligne = ligneDeDossier(zone);
      return ligne ? premier(repere('dossier-tot'), ligne) : null;
    }
    case 'dossier-maintenant':
      // La prochaine chose d'une ligne et son « Continuer » (chantier `aide`, ticket 02) : le
      // repère n'est posé que sur une ligne qui en a une : la première trouvée.
      return premier(repere('dossier-maintenant'));
    case 'dossier-nouveau':
      // Sans projet (ticket 14), la carte « Ouvrir un dossier » de « Maintenant », ou « Ton
      // premier dossier » de « Projets » : les deux pages vides ont la même.
      return premier(repere('dossier-nouveau')) ?? premier(repere('premier-carte'));
    case 'champ-nouveau-dossier':
      return (
        premier(`${repere('dossier-nouveau')} ${repere('champ-nouveau-dossier')}`) ??
        premier(`${repere('premier-carte')} ${repere('champ-nouveau-dossier')}`)
      );
    // Ticket 44 : la poubelle d'UN dossier et la confirmation de SA suppression, par son
    // identifiant, jamais par son titre (un dossier homonyme n'est jamais entouré).
    case 'dossiers-exemples':
      // « Projets » sans projet : les trois lignes d'exemple, en pâle (ticket 14).
      return premier(repere('dossiers-exemples'));
    case 'dossier-supprimer':
      return premier(
        `${repere('dossier')}[data-dossier="${CSS.escape(zone.id ?? '')}"] ${repere('dossier-supprimer')}`
      );
    case 'supprimer-confirmer':
      return premier(
        `${repere('supprimer')}[data-dossier="${CSS.escape(zone.id ?? '')}"] ${repere('supprimer-confirmer')}`
      );

    // ── une feuille ──
    case 'bulle':
      return (
        premier(`${repere('bulle')}[data-bulle="${CSS.escape(zone.id ?? '')}"]`) ??
        premier(`[data-bulle="${CSS.escape(zone.id ?? '')}"]`)
      );
    case 'bascule-liste':
      return premier(repere('bascule-liste'));
    case 'bascule-carte':
      return premier(repere('bascule-carte'));
    case 'bascule-hierarchie':
      return premier(repere('bascule-hierarchie'));
    case 'hierarchiser':
      // En Carte, le bouton « Hiérarchiser » de la barre de la carte (chantier `revue-design`,
      // ticket 04) ; en Hiérarchie, dans le menu ⚙ s'il est ouvert, sinon le bouton ⚙ qui
      // l'ouvre (ticket 13). En Liste, il n'y a pas de ⚙ (ticket 03) : l'onglet « Hiérarchie ».
      return (
        premier(repere('hierarchiser')) ??
        premier(repere('parametres-de-feuille')) ??
        premier(repere('bascule-hierarchie'))
      );
    case 'profondeur':
      // Un niveau nommé (« 2 », chantier `aide`, ticket 07) : son chiffre, tant qu'il n'est pas
      // choisi ; choisi, rien, et l'anneau passe à la zone suivante.
      if (zone.texte) {
        const niveau = [...document.querySelectorAll<HTMLElement>(repere('profondeur-losange'))]
          .filter(visible)
          .find((b) => net(b.textContent) === net(zone.texte));
        return niveau && niveau.getAttribute('aria-pressed') !== 'true' ? niveau : null;
      }
      // Le losange allumé du sélecteur (ticket 13) : le recliquer referme tout.
      return premier(`${repere('profondeur-losange')}[aria-pressed="true"]`) ?? premier(repere('profondeur'));
    case 'fil': {
      const boutons = [...document.querySelectorAll(repere('fil'))].filter(visible);
      return boutons.find((b) => net(b.textContent) === net(zone.texte)) ?? null;
    }
    case 'barre-ajout':
      return premier(`${repere('feuille-barre')} ${repere('barre-ajout')}`) ?? premier(repere('barre-champ'));
    // ── la fiche ──
    case 'fiche-fermer':
      return premier(repere('fiche-fermer'));
    case 'fiche-parametres':
      return premier(repere('fiche-parametres'));
    case 'fiche-titre':
      // Le champ s'il est ouvert, sinon le titre qui l'ouvre d'un clic (ticket 10).
      return premier(dansLaFiche('fiche-titre-champ')) ?? premier(dansLaFiche('fiche-titre'));
    case 'fiche-difficulte': {
      // La cinquième étoile de « Difficulté » (chantier `aide`, ticket 04) : la dernière.
      const bloc = premier(dansLaFiche('fiche-difficulte'));
      return bloc ? ([...bloc.querySelectorAll('button')].filter(visible).at(-1) ?? bloc) : null;
    }
    case 'fiche-choix': {
      // Un choix du réglage déplié sous les pastilles (« Important », chantier `aide`, ticket 07),
      // par son mot exact, seulement dans la fiche : le filtre de la carte, derrière, porte le même.
      const deplie = premier(dansLaFiche('parametres'));
      if (!deplie) return null;
      return [...deplie.querySelectorAll('button')].filter(visible).find((b) => libelle(b.textContent) === libelle(zone.texte)) ?? null;
    }
    case 'duree-heures':
      // La case des heures de « Ça me prend », ouverte par « + Durée » (chantier duree-a-la-minute, ticket 04).
      return premier(dansLaFiche('duree-heures'));
    case 'supprimer-bulle-confirmer':
      // La confirmation de CETTE bulle, par son identifiant (`SupprimerBulle.tsx`).
      return premier(
        `${repere('supprimer')}[data-bulle="${CSS.escape(zone.id ?? '')}"] ${repere('supprimer-confirmer')}`
      );
    case 'ligne-parametre': {
      // Une ligne des paramètres ouverts (« Le lieu », « Le lien »…), par son nom.
      const lignes = [...document.querySelectorAll(`${repere('fiche')} ${repere('parametres')} ${repere('champs-ligne')}`)].filter(visible);
      const ligne = lignes.find((l) => libelle(l.querySelector(repere('champs-nom'))?.textContent).startsWith(libelle(zone.texte)));
      if (ligne) return premier(repere('champs-valeur'), ligne) ?? ligne;
      // Fermée : le bouton d'« Ajouter » qui l'ouvre (chantier `revue-design`, ticket 02).
      switch (libelle(zone.texte)) {
        case 'le lieu':
          return premier(dansLaFiche('ajouter-adresse'));
        case 'le contact':
          return premier(dansLaFiche('ajouter-contact'));
        case 'le lien':
          return premier(dansLaFiche('ajouter-lien'));
        default:
          return null;
      }
    }
    case 'champ-notes':
      return premier(dansLaFiche('champ-notes'));
    case 'champ-delai':
      return premier(dansLaFiche('champ-delai'));
    // Ticket 17 : la date qu'on pose, une fois « En poser une » cliqué, et le champ où l'on
    // cherche la bulle à lier, avant qu'un résultat ne s'affiche.
    case 'champ-date':
      return premier(dansLaFiche('champ-date'));
    case 'champ-lier':
      return premier(dansLaFiche('champ-lier')) ?? premier(repere('champ-lier'));
    case 'resultat-lier':
      return premier(repere('resultat-lier'));

    // ── Agenda ──
    case 'agenda-semaines':
      return premier(repere('agenda-semaines'));
    // Le Calendrier, qui s'ouvre (chantier `revue-design`, ticket 06 ; chantier `aide`, ticket 02).
    case 'agenda-calendrier':
      return premier(repere('agenda-calendrier'));
    // La case d'aujourd'hui, qui ouvre « Préparer ma journée » (chantier `aide`, ticket 05).
    case 'agenda-aujourdhui':
      return premier(repere('agenda-aujourdhui'));
    case 'agenda-envoyer':
      // Le bandeau du bas est parti (chantier revue-design, ticket 06) : l'export est en haut.
      return premier(repere('agenda-export'));
    // L'Agenda vide et ses deux vues (ticket 15 de retours-testeurs).
    case 'agenda-presentation':
      return premier(repere('agenda-vues'));

    case 'bouton':
      return zone.texte ? boutonNomme(zone.texte) : null;
    default: {
      // Toutes les zones sont traitées : une nouvelle clé sans son cas ne compile pas.
      const jamais: never = zone.cle;
      return jamais;
    }
  }
}

/**
 * Le prochain geste, dans « Préparer ma journée » (`PreparerUnJour.tsx`, un seul panneau depuis
 * le chantier `revue-design`, ticket 06), pour y mettre cette tâche (ticket 17 de
 * retours-testeurs) : déjà dans la journée, « C'est ma journée » ; montrée dans « À ajouter »
 * ou dans la recherche, son + ; sinon « Chercher une tâche », pour aller la trouver. `null` sans
 * panneau ouvert.
 */
function dansLePanneauDuJour(id: string): HTMLElement | null {
  const panneau = premier(repere('preparer'));
  if (!panneau) return null;
  const fort = premier(repere('preparer-fort'), panneau);
  const dansLaJournee = [...panneau.querySelectorAll<HTMLElement>(`${repere('preparer-creneau')}[data-tache]`)].some(
    (el) => el.dataset.tache === id
  );
  if (dansLaJournee) return fort;
  const aAjouter =
    [...panneau.querySelectorAll<HTMLElement>(`${repere('preparer-tache')}[data-tache]`)]
      .filter(visible)
      .find((el) => el.dataset.tache === id) ?? null;
  // Trop longue pour ce qui reste (chantier `aide`, ticket 05) : d'abord plus de temps.
  if (aAjouter?.dataset.deTrop === 'oui') return plusDeTemps(panneau) ?? aAjouter;
  return aAjouter ?? premier(repere('preparer-chercher'), panneau) ?? fort;
}

/** Le temps de la journée : déplié, le choix juste plus long que celui pris ; sinon le bouton qui le déplie. */
function plusDeTemps(panneau: HTMLElement): HTMLElement | null {
  const choix = [...panneau.querySelectorAll<HTMLElement>(repere('preparer-temps-choix'))].filter(visible);
  if (choix.length > 0) {
    const pris = choix.findIndex((b) => b.getAttribute('aria-pressed') === 'true');
    return choix[pris + 1] ?? choix.at(-1) ?? null;
  }
  return premier(repere('preparer-temps'), panneau);
}

/** La première des zones trouvée à l'écran : l'anneau suit le geste où l'on en est. */
export function trouverLaPremiere(zones: ZoneDuTuto[]): HTMLElement | null {
  for (const zone of zones) {
    const el = trouverLaZone(zone);
    if (el) return el;
  }
  return null;
}
